import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import { z } from 'zod';

import type { ToastProps } from '@motorway/mw-highway-code/pre-alpha';
import { toast as showToast } from '@motorway/mw-highway-code/pre-alpha';

import type { Order } from 'Types/generic';
import ROUTES from 'Utilities/routes';

import { getSortBy, getSortOrder } from './useInsightsRouter.helpers';

const vehicleSchema = z
	.object({
		make: z.string(),
		model: z.string(),
	})
	.optional()
	.catch(undefined);

const pageSchema = z.coerce.number().gte(1).catch(1);

type GoToLandingPage = ({ toast }: { toast?: ToastProps }) => Promise<void>;
type GoToResultsPage = (params: { make: string; model: string; persistSorting: boolean }) => Promise<void>;
type UpdateResultsQueryParams = (params: { newQuery: ParsedUrlQuery }) => Promise<void>;
type PrefetchResultsPage = (params: { make: string; model: string }) => Promise<void>;

type UseInsightsRouterReturn = {
	goToLandingPage: GoToLandingPage;
	goToResultsPage: GoToResultsPage;
	page: number;
	prefetchResultsPage: PrefetchResultsPage;
	sortBy: string;
	sortOrder: Order;
	updateResultsQueryParams: UpdateResultsQueryParams;
	vehicle?: z.infer<typeof vehicleSchema>;
};

export const useInsightsRouter = (): UseInsightsRouterReturn => {
	const { prefetch, push, query } = useRouter();

	const { make, model, ...restOfQuery } = query;
	const { page, sortBy, sortOrder } = restOfQuery;

	const vehicle = vehicleSchema.parse({ make, model });
	const pageNumber = pageSchema.parse(page);

	const parsedSortBy = getSortBy(sortBy);
	const parsedSortOrder = getSortOrder(sortOrder);

	const goToLandingPage: GoToLandingPage = async ({ toast }) => {
		await push(ROUTES.INSIGHTS_LANDING.as());

		if (typeof toast !== 'undefined') {
			showToast(toast);
		}
	};

	const goToResultsPage: GoToResultsPage = async ({ make: newMake, model: newModel, persistSorting }) => {
		await push(
			{
				pathname: ROUTES.INSIGHTS_RESULTS.as(newMake, newModel),
				query: persistSorting
					? {
							...restOfQuery,
							page: 1,
					  }
					: undefined,
			},
			undefined,
			{ shallow: persistSorting },
		);
	};

	const updateResultsQueryParams: UpdateResultsQueryParams = async ({ newQuery }) => {
		await push(
			{
				pathname: ROUTES.INSIGHTS_RESULTS.as(make as string, model as string),
				query: {
					...restOfQuery,
					...newQuery,
					page: 1,
				},
			},
			undefined,
			{ shallow: true },
		);
	};

	const prefetchResultsPage: PrefetchResultsPage = async ({ make: newMake, model: newModel }) => {
		await prefetch(ROUTES.INSIGHTS_RESULTS.as(newMake, newModel));
	};

	return {
		goToLandingPage,
		goToResultsPage,
		page: pageNumber,
		prefetchResultsPage,
		sortBy: parsedSortBy,
		sortOrder: parsedSortOrder,
		updateResultsQueryParams,
		vehicle,
	};
};
